import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { CashCategory, CashCategoryKind, PaginatedCashCategories } from '@/types/cash'
import type { CashCategoryFormData } from '@/validators/cash'

export interface ListCashCategoryParams {
  search?: string
  kind?: CashCategoryKind
  active?: boolean
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListCashCategoryParams) => ['cash-categories', params] as const,
}

export function useCashCategories(params: ListCashCategoryParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<PaginatedCashCategories>('/cash-categories', { params }).then((r) => r.data),
  })
}

export function useCreateCashCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CashCategoryFormData) =>
      api.post<CashCategory>('/cash-categories', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-categories'] }),
  })
}

export function useUpdateCashCategory(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string }) =>
      api.put<CashCategory>(`/cash-categories/${id}`, data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-categories'] }),
  })
}

export function useDeactivateCashCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<CashCategory>(`/cash-categories/${id}/deactivate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-categories'] }),
  })
}

export function useActivateCashCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<CashCategory>(`/cash-categories/${id}/activate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-categories'] }),
  })
}
